import { ButtonProps, ButtonType, DialogFooterButtonsWidget } from '@tableau/widgets';
import * as React from 'react';

import { Messages } from '../../codegen/Localize';
import { IDataSourceBase } from '../../ts/models/IDataSourceInfo';

export interface PublishCompleteDialogProps {
  dismiss: () => void;
  close: () => void;
  onDevicePreviewClick: () => void;
  workbookTitle: string;
  datasources: IDataSourceBase[];
  getDatasourceIconClass: (datasource: IDataSourceBase) => string;
  publishLiveQueryInfo: string;
  shouldDisplayDevicePreview: boolean;
  shouldDisplayLiveQueryInfo: boolean;
  shouldDisplayScheduleButton: boolean;
}

interface DatasourceProps {
  name: string;
  datasourceIconClass: string;
}

export namespace PublishCompleteDialog {
  export function Title(): string {
    return Messages.publishDatasourceSuccessDialogTitleComplete();
  }

  export function Content(props: PublishCompleteDialogProps) {
    return (
      <div className="tb-workbook-publish-success-modal-with-preview">
        <div className="tb-dialog-body-text">
          <div className="tb-published-items">
            <WorkbookSection {...props} />
            <DatasourceSection {...props} />
          </div>
          <TableauBridgeBox {...props}/>
        </div>
      </div>
    );
  }

  export function WorkbookSection(props: PublishCompleteDialogProps) {
    return (
      <div className="tb-workbook">
        <span className="tb-workbook-title">{Messages.publishWorkbookSuccessDialogText_workbookHeader()}</span>
        <div className="tb-workbook-body">
          <div className="tb-published-item">
            <div className="tb-workbook-wrapper">
              <div className="tb-published-icon tb-dialogs-workbook" />
              <div className="tb-published-name">{props.workbookTitle}</div>
            </div>
            <DevicePreviewLink {...props} />
          </div>
        </div>
      </div>
    );
  }

  export function DevicePreviewLink(props: PublishCompleteDialogProps) {
    if (!props.shouldDisplayDevicePreview) {
      return null;
    }

    return (
      <div className="tb-device-preview">
        <div className="tb-device-preview-text">{Messages.publishWorkbookSuccessDialogText_devicePreviewMessage()}</div>
        <a className="tb-device-preview-link" onClick={props.onDevicePreviewClick}>{Messages.publishWorkbookSuccessDialogText_devicePreviewLink()}</a>
      </div>
    );
  }

  export function DatasourceSection(props: PublishCompleteDialogProps) {
    if (props.datasources.length === 0 && props.shouldDisplayScheduleButton === false) {
      return null;
    }

    return (
      <div className="tb-datasources">
        <span className="tb-datasources-title">{Messages.publishWorkbookSuccessDialogText_datasourcesHeader()}</span>
        <div className="tb-datasources-body">
          {props.datasources.map((item, index) => <Datasource {...{name: item.name, datasourceIconClass: props.getDatasourceIconClass(item), key: index}}/>)}
          <ScheduleRefreshLink {...props}/>
        </div>
      </div>
    );
  }

  export function Datasource(props: DatasourceProps): JSX.Element {
    const iconClass = `tb-published-icon ${props.datasourceIconClass}`;
    return (
      <div className="tb-published-item">
        <div className="tb-datasource-wrapper">
          <div className={iconClass} />
          <div className="tb-published-name">{props.name}</div>
        </div>
      </div>
    );
  }

  export function ScheduleRefreshLink(props: PublishCompleteDialogProps) {
    if (props.shouldDisplayScheduleButton === false) {
      return null;
    }

    return (
      <div className="tb-data-refresh">
        <div className="tb-data-refresh-text">{Messages.publishWorkbookSuccessDialogText_scheduleMessage()}</div>
        <a className="tb-data-refresh-link" onClick={props.close}>{Messages.publishWorkbookSuccessDialogText_scheduleLink()}</a>
      </div>
    );
  }

  export function TableauBridgeBox(props: PublishCompleteDialogProps) {
    if (!props.shouldDisplayLiveQueryInfo) {
      return null;
    }

    return (
      <div className="tb-box tb-publish-details">
        <span>{props.publishLiveQueryInfo}</span>
        &nbsp;
        <span
            tb-popover="'datasourcePublishSuccessWhatIsSync.html'"
            tb-popover-class="tb-popover-wrapper-short-height"
            tb-popover-delay="150"
            className="tb-datasource-publish-details-popover-trigger">
          {Messages.publishDatasourceSuccessDialogText_onlineSync_whatsthis()}
        </span>
      </div>
    );
  }

  export function Footer(props: PublishCompleteDialogProps) {
    const CloseButtonProps: ButtonProps = {
      buttonType: ButtonType.Outline,
      handleClick: props.dismiss,
      testId: 'publish-success-done',
      children: Messages.publishWorkbookSuccessDialogButton_done()
    };

    return <DialogFooterButtonsWidget buttons={[ CloseButtonProps ]} testId="publish-complete-dialog-footer" />;
  }
}
